// Pure CSV parsing for the bulk-contact import — no network/auth deps so it's
// unit-testable in isolation (csv-import.test.ts). The network fan-out that
// turns these rows into contacts lives in api.ts (importContactRows).

// Required header columns (case/space-insensitive). The CSV the user uploads is
// `full_name,phone_number,client` — `client` is the client's NAME, not an id.
export interface CsvContactRow {
  fullName: string;
  phoneNumber: string;
  client: string;
}

// A minimal RFC-4180 CSV parser: handles quoted fields, embedded commas/newlines,
// and "" escapes. Returns rows of raw string cells (header included).
export function parseCsv(text: string): string[][] {
  const rows: string[][] = [];
  let row: string[] = [];
  let field = '';
  let inQuotes = false;
  // Strip a leading UTF-8 BOM so the first header cell matches cleanly.
  const src = text.charCodeAt(0) === 0xfeff ? text.slice(1) : text;

  for (let i = 0; i < src.length; i++) {
    const ch = src[i];
    if (inQuotes) {
      if (ch === '"') {
        if (src[i + 1] === '"') {
          field += '"';
          i++;
        } else {
          inQuotes = false;
        }
      } else {
        field += ch;
      }
    } else if (ch === '"') {
      inQuotes = true;
    } else if (ch === ',') {
      row.push(field);
      field = '';
    } else if (ch === '\n' || ch === '\r') {
      // Treat \r, \n, and \r\n as one row terminator.
      if (ch === '\r' && src[i + 1] === '\n') i++;
      row.push(field);
      rows.push(row);
      row = [];
      field = '';
    } else {
      field += ch;
    }
  }
  // Flush the trailing field/row (file may not end with a newline).
  if (field !== '' || row.length > 0) {
    row.push(field);
    rows.push(row);
  }
  return rows;
}

const normHeader = (h: string): string => h.trim().toLowerCase().replace(/\s+/g, '_');

// Map a parsed CSV (with header) to typed rows. Throws if a required column is
// missing so the caller can show a single clear error before importing anything.
export function rowsFromCsv(text: string): CsvContactRow[] {
  const grid = parseCsv(text).filter((r) => r.some((c) => c.trim() !== '')); // drop blank lines
  const headerRow = grid[0];
  if (!headerRow) throw new Error('The file is empty.');

  const header = headerRow.map(normHeader);
  const col = (name: string) => header.indexOf(name);
  const iName = col('full_name');
  const iPhone = col('phone_number');
  const iClient = col('client');
  const missing = [
    iName === -1 && 'full_name',
    iPhone === -1 && 'phone_number',
    iClient === -1 && 'client',
  ].filter(Boolean);
  if (missing.length)
    throw new Error(
      `Missing column${missing.length === 1 ? '' : 's'}: ${missing.join(', ')}. ` +
        `Expected header: full_name,phone_number,client`,
    );

  return grid.slice(1).map((r) => ({
    fullName: (r[iName] ?? '').trim(),
    phoneNumber: (r[iPhone] ?? '').trim(),
    client: (r[iClient] ?? '').trim(),
  }));
}
